/**
 * ApplyPage - Access application page
 *
 * APPLICANT MODE: User verified their email via OTP, shows form to complete application.
 * Phone number is collected and confirmed (re-entered) but NOT verified via SMS.
 * SMS notifications are only sent when the application is accepted by an admin.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { submitApplication } from '@/services/intercom/intercomService'
import {
  hideLauncher,
  hideMessenger
} from '@/services/intercom/intercomMessenger'
import { applyPageConfig } from '@/config/applyConfig'
import { ApplicationConfirmation } from '@/components/auth/ApplicationConfirmation'
import type { ApplicantUser } from '@/features/auth/types'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

export function ApplyPage() {
  const navigate = useNavigate()
  const { applicant, logoutApplicant } = useAuth()

  if (applicant) {
    return (
      <ApplicantApplyView
        applicant={applicant}
        onLogout={() => {
          logoutApplicant()
          navigate('/login')
        }}
      />
    )
  }

  navigate('/login')
  return null
}

// ============================================================================
// APPLICANT APPLY VIEW
// ============================================================================

interface ApplicantApplyViewProps {
  applicant: ApplicantUser
  onLogout: () => void
}

function ApplicantApplyView({ applicant, onLogout }: ApplicantApplyViewProps) {
  // Form fields
  const [firstName, setFirstName] = useState(applicant.firstName || '')
  const [lastName, setLastName] = useState(applicant.lastName || '')
  const [phoneNumber, setPhoneNumber] = useState(applicant.phoneNumber || '')
  const [phoneConfirmed, setPhoneConfirmed] = useState(false)
  const [phoneVerifying, setPhoneVerifying] = useState(false)
  const [reason, setReason] = useState('')
  const [ageConfirmed, setAgeConfirmed] = useState(false)

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submittedApplicationNumber, setSubmittedApplicationNumber] = useState<number | null>(null)

  // ============================================================================
  // CLEANUP: Hide Intercom when leaving /apply page
  // ============================================================================
  useEffect(() => {
    return () => {
      hideLauncher()
      hideMessenger()
    }
  }, [])

  // ============================================================================
  // ALREADY SUBMITTED: If application status is 'pending', show welcome back
  // ============================================================================
  if (applicant.status === 'pending') {
    return (
      <ApplicationConfirmation
        applicationId={applicant.applicationId}
        applicationNumber={null}
        applicantEmail={applicant.email}
        applicantFirstName={applicant.firstName}
        applicantPhone={applicant.phoneNumber}
        onLogout={onLogout}
      />
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name')
      return
    }

    if (!reason.trim() || reason.trim().length < 10) {
      setError('Please tell us more about why you want to join (minimum 10 characters)')
      return
    }

    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number')
      return
    }

    if (!phoneConfirmed) {
      setError('Please verify your phone number')
      return
    }

    if (!ageConfirmed) {
      setError('Please confirm you are 18 or older')
      return
    }

    setIsSubmitting(true)

    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`
      const result = await submitApplication({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: fullName,
        email: applicant.email,
        reason: reason.trim(),
        phoneNumber: phoneNumber || undefined,
        applicationId: applicant.applicationId
      })

      if (!result.success) {
        setError(result.error || 'Failed to submit application')
        setIsSubmitting(false)
        return
      }

      if (result.applicationNumber) {
        setSubmittedApplicationNumber(result.applicationNumber)
      }

      const updatedApplicant = {
        ...applicant,
        status: 'pending' as const,
        name: fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber || null
      }
      localStorage.setItem('applicant_data', JSON.stringify(updatedApplicant))

      setSubmitted(true)
    } catch (err) {
      console.error('[ApplyPage] Submit error:', err)
      setError('Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <ApplicationConfirmation
        applicationId={applicant.applicationId}
        applicationNumber={submittedApplicationNumber}
        applicantEmail={applicant.email}
        applicantFirstName={applicant.firstName}
        applicantPhone={applicant.phoneNumber}
        onLogout={onLogout}
      />
    )
  }

  return (
    <div className="min-h-screen starry-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-black">
            {applyPageConfig.title}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Complete your application to join
          </p>
        </div>

        {/* Application Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 overflow-hidden">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (read-only, verified) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (verified)
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center border border-gray-200 rounded-md overflow-hidden">
                  <input
                    type="email"
                    value={applicant.email}
                    disabled
                    className="flex-1 px-3 py-2 bg-gray-50 text-gray-500 border-none outline-none"
                  />
                  <span className="px-3 py-2 bg-green-50 text-green-600 text-sm">
                    ✓
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  className="text-sm text-[#6b7850] hover:text-[#5c5b44] transition-colors"
                >
                  Change
                </button>
              </div>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                {applyPageConfig.fields.firstName.label}
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={applyPageConfig.fields.firstName.placeholder}
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6b7850] focus:border-[#6b7850] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                {applyPageConfig.fields.lastName.label}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={applyPageConfig.fields.lastName.placeholder}
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6b7850] focus:border-[#6b7850] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Phone Number with Verify Lock */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                {applyPageConfig.fields.phone.label}
              </label>
              <div className="flex items-center gap-2">
                <PhoneInput
                  id="phoneNumber"
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="US"
                  value={phoneNumber}
                  onChange={(value) => {
                    setPhoneNumber(value || '')
                    setPhoneConfirmed(false)
                  }}
                  placeholder={applyPageConfig.fields.phone.placeholder}
                  disabled={isSubmitting || phoneConfirmed || phoneVerifying}
                  className={`flex-1 min-w-0 phone-input-custom ${phoneConfirmed ? 'phone-input-disabled' : ''}`}
                />

                {/* Verify button — locks the number permanently */}
                {!phoneConfirmed && !phoneVerifying && phoneNumber && isValidPhoneNumber(phoneNumber) && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoneVerifying(true)
                      setTimeout(() => {
                        setPhoneVerifying(false)
                        setPhoneConfirmed(true)
                      }, 2000)
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-gradient-to-r from-[#5c5b44] to-[#6b7850] text-white text-sm rounded-md hover:from-[#5c5b44] hover:to-[#6b7850] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Verify
                  </button>
                )}

                {/* Loading spinner while verifying */}
                {phoneVerifying && (
                  <span className="px-3 py-2 text-[#6b7850] text-sm font-medium whitespace-nowrap animate-pulse">
                    Verifying...
                  </span>
                )}

                {/* Verified badge — permanent, no change option */}
                {phoneConfirmed && (
                  <span className="px-3 py-2 bg-green-50 text-green-600 text-sm rounded-md border border-green-200 font-medium whitespace-nowrap">
                    ✓ Verified
                  </span>
                )}
              </div>
            </div>

            {/* Reason */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                {applyPageConfig.fields.reason.label}
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={applyPageConfig.fields.reason.placeholder}
                required
                minLength={applyPageConfig.fields.reason.minLength}
                maxLength={applyPageConfig.fields.reason.maxLength}
                rows={applyPageConfig.fields.reason.rows}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6b7850] focus:border-[#6b7850] resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Age confirmation - members must be adults */}
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="age-confirm"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                disabled={isSubmitting}
                required
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-pink-600"
              />
              <span>I confirm I am <strong>18 years or older</strong></span>
            </label>

            {/* Error message */}
            {error && (
              <div className="text-red-600 text-sm animate-fade-in">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || !phoneConfirmed}
              className="w-full py-3 bg-gradient-to-r from-[#5c5b44] to-[#6b7850] text-white rounded-lg font-bold hover:from-[#5c5b44] hover:to-[#6b7850] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : applyPageConfig.submitButton.label}
            </button>

            {/* Back to login link */}
            <button
              type="button"
              onClick={onLogout}
              className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm transition-colors"
            >
              Use a different email
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
